/**
 * Mana Locals Admin — M08 VENDOR VERIFICATION boundary schemas.
 *
 * CONTRACT SOURCE: `contracts/openapi/vendor-verification.v1.yaml` (API repository).
 * These shapes mirror the API's M08 verification slice and are validated on every
 * response.
 *
 * TWO PROJECTIONS
 * ---------------
 *   - `VerificationCaseSchema` — the VENDOR-visible projection. It WITHHOLDS the
 *     three account identifiers (`submittedByAccountId`, `decidedByAccountId`,
 *     `attachedByAccountId`).
 *   - `VerificationCaseReviewSchema` — the VERIFIER projection. It releases exactly
 *     those three identifiers. Obtaining it requires `vendor.verification.case.review`
 *     and writes one `audit_log` row per request.
 *
 * The platform review surface (`GET .../review`, `POST .../decision`) selects the
 * tenant with the `X-Tenant-Id` header and requires the platform grant
 * `tenant.verification.review`.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from '@/api/schemas/common';

// ---------------------------------------------------------------------------
// Closed vocabularies (mirror the database CHECK constraints)
// ---------------------------------------------------------------------------

export const SubjectKindSchema = z.enum(['legal_entity', 'location', 'representative']);
export type SubjectKind = z.infer<typeof SubjectKindSchema>;

export const VerificationTypeSchema = z.enum([
  'business_registration',
  'license_or_permit',
  'insurance_policy',
  'professional_credential',
  'representative_authority',
]);
export type VerificationType = z.infer<typeof VerificationTypeSchema>;

export const VerificationStatusSchema = z.enum(['draft', 'submitted', 'decided', 'expired']);
export type VerificationStatus = z.infer<typeof VerificationStatusSchema>;

export const VerificationDecisionSchema = z.enum([
  'approved',
  'rejected',
  'more_evidence_required',
]);
export type VerificationDecision = z.infer<typeof VerificationDecisionSchema>;

export const VerificationReasonCodeSchema = z.enum([
  'evidence_incomplete',
  'evidence_invalid',
  'evidence_expired',
  'subject_mismatch',
  'authority_not_demonstrated',
]);
export type VerificationReasonCode = z.infer<typeof VerificationReasonCodeSchema>;

export const VerificationStateSchema = z.enum([
  'unverified',
  'pending',
  'verified',
  'expired',
  'rejected',
]);
export type VerificationState = z.infer<typeof VerificationStateSchema>;

// ---------------------------------------------------------------------------
// Evidence
// ---------------------------------------------------------------------------

export const VerificationEvidenceSchema = z
  .object({
    evidenceId: UuidSchema,
    documentId: UuidSchema,
    validFrom: IsoDateTimeSchema.nullable(),
    validTo: IsoDateTimeSchema.nullable(),
    attachedAt: IsoDateTimeSchema,
  })
  .strict();
export type VerificationEvidence = z.infer<typeof VerificationEvidenceSchema>;

/** ONE evidence row as the VERIFIER sees it: the vendor row plus the attaching account. */
export const VerificationEvidenceReviewSchema = VerificationEvidenceSchema.extend({
  attachedByAccountId: UuidSchema,
}).strict();
export type VerificationEvidenceReview = z.infer<typeof VerificationEvidenceReviewSchema>;

// ---------------------------------------------------------------------------
// The case projections
// ---------------------------------------------------------------------------

/** The VENDOR-visible projection. Withholds the three account identifiers. */
export const VerificationCaseSchema = z
  .object({
    caseId: UuidSchema,
    subjectKind: SubjectKindSchema,
    subjectId: UuidSchema,
    type: VerificationTypeSchema,
    status: VerificationStatusSchema,
    decision: VerificationDecisionSchema.nullable(),
    reasonCode: VerificationReasonCodeSchema.nullable(),
    expiresAt: IsoDateTimeSchema.nullable(),
    expiredAt: IsoDateTimeSchema.nullable(),
    submittedAt: IsoDateTimeSchema.nullable(),
    decidedAt: IsoDateTimeSchema.nullable(),
    evidence: z.array(VerificationEvidenceSchema).max(20),
    version: z.number().int().min(1),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
  })
  .strict();
export type VerificationCase = z.infer<typeof VerificationCaseSchema>;

/** The VERIFIER projection. Releases exactly the three account identifiers. */
export const VerificationCaseReviewSchema = VerificationCaseSchema.extend({
  submittedByAccountId: UuidSchema.nullable(),
  decidedByAccountId: UuidSchema.nullable(),
  evidence: z.array(VerificationEvidenceReviewSchema).max(20),
}).strict();
export type VerificationCaseReview = z.infer<typeof VerificationCaseReviewSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

/**
 * The decision body. `reasonCode` is REQUIRED unless the decision is `approved`,
 * and MUST be absent when it is. `expiresAt` must be absent unless approved.
 */
export const DecideVerificationCaseRequestSchema = z
  .object({
    decision: VerificationDecisionSchema,
    reasonCode: VerificationReasonCodeSchema.optional(),
    expiresAt: IsoDateTimeSchema.optional(),
  })
  .strict()
  .refine(
    (b) => (b.decision === 'approved' ? b.reasonCode === undefined : b.reasonCode !== undefined),
    {
      message:
        'reasonCode is required unless the decision is approved, and must be absent when it is',
      path: ['reasonCode'],
    },
  )
  .refine((b) => (b.decision === 'approved' ? true : b.expiresAt === undefined), {
    message: 'expiresAt must be absent unless the decision is approved',
    path: ['expiresAt'],
  });
export type DecideVerificationCaseRequest = z.infer<typeof DecideVerificationCaseRequestSchema>;

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

const MetaSchema = z.object({ requestId: z.string().min(1).max(128) }).strict();

export const VerificationCaseResponseSchema = z
  .object({ data: VerificationCaseSchema, meta: MetaSchema })
  .strict();
export type VerificationCaseResponse = z.infer<typeof VerificationCaseResponseSchema>;

export const VerificationCaseReviewResponseSchema = z
  .object({ data: VerificationCaseReviewSchema, meta: MetaSchema })
  .strict();
export type VerificationCaseReviewResponse = z.infer<typeof VerificationCaseReviewResponseSchema>;

/** The closed error-code vocabulary for this surface. */
export const VerificationErrorCodeSchema = z.enum([
  'INVALID_VERIFICATION_CASE',
  'INVALID_VERIFICATION_EVIDENCE',
  'INVALID_VERIFICATION_DECISION',
  'VERIFICATION_CASE_NOT_FOUND',
  'VERIFICATION_CASE_CONFLICT',
  'INVALID_VERIFICATION_TRANSITION',
  'VERIFICATION_EVIDENCE_REQUIRED',
  'VERSION_CONFLICT',
  'IDEMPOTENCY_KEY_REQUIRED',
  'IDEMPOTENCY_KEY_REUSED',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'RATE_LIMITED',
  'SERVICE_UNAVAILABLE',
]);
export type VerificationErrorCode = z.infer<typeof VerificationErrorCodeSchema>;
